import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../i18n";
import { useAuth } from "../AuthProvider";
import { getActiveOrgId } from "../../utils/orgUtils";
import { buildCloudRequestUrl, readProposalQueueResponse } from "../integrations/gcpDiscoverySession.mjs";
import { Button } from "../ui/Button";
import { Select } from "../ui/Select";
import { TextField } from "../ui/TextField";
import { GovernanceListingSection } from "../governance/GovernanceListingSection";
import listStyles from "@/styles/pages/governance/GovernanceListing.module.scss";
import cardStyles from "./CloudAssetProposalsCard.module.scss";
import {
  CRITICALITY_EMPTY_LABEL,
  PROPOSAL_COLUMN_WIDTH,
  categoryPresentation,
  filterProposalRows,
  matchingRegistrationCount,
  proposalActionVisibility,
  proposalTableColumns,
  registrationBody,
  showServerCategoryAction,
} from "./cloudProposalPresentation.mjs";

type Proposal = {
  id: string;
  resourceName: string;
  projectId?: string | null;
  kind: string;
  location?: string | null;
  ip?: string | null;
  status: string;
  proposedCategory?: string | null;
  classificationBasis: string;
  eligible: boolean;
  assetId?: string | null;
  externalId: string;
  inventoryOnly?: boolean;
};

type Counts = {
  new?: number;
  pendingVms?: number;
  decided?: number;
  decidedVms?: number;
  ignored?: number;
  ignoredVms?: number;
};

type Page = {
  items: Proposal[];
  nextCursor?: string | null;
  totalNew?: number;
  counts?: Counts | null;
  notice?: { projectId?: string; notifiedAt?: string } | null;
  error?: string | null;
};

type Member = { id: string; username: string; email?: string };
type ConfirmMode = "selected" | "all" | null;

const COLUMN_LABEL: Record<string, [string, string]> = {
  name: ["gcpIntegration.name", "Name"],
  project: ["gcpProposals.project", "Project"],
  kind: ["gcpIntegration.kind", "Kind"],
  category: ["gcpProposals.category", "Category"],
  zone: ["gcpProposals.zone", "Zone"],
  ip: ["gcpProposals.ip", "IP"],
  status: ["gcpChecks.status", "Status"],
  actions: ["gcpProposals.actions", "Actions"],
};

export default function CloudAssetProposalsCard() {
  const { t } = useI18n();
  const { token } = useAuth();
  const [page, setPage] = useState<Page>({ items: [] });
  const [selected, setSelected] = useState<string[]>([]);
  const [kind, setKind] = useState("INSTANCE");
  const [status, setStatus] = useState("NEW");
  const [query, setQuery] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [environment, setEnvironment] = useState("PRODUCTION");
  const [criticality, setCriticality] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [users, setUsers] = useState<Member[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [confirmMode, setConfirmMode] = useState<ConfirmMode>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [noticeHidden, setNoticeHidden] = useState(false);

  useEffect(() => {
    let active = true;
    const load = () => {
      const orgId = getActiveOrgId();
      setNoticeHidden(false);
      setSelected([]);
      if (!token || !orgId) {
        setPage({ items: [], counts: null, error: "ORG_SCOPE_REQUIRED" });
        return;
      }
      const path = `/asset-proposals?status=${encodeURIComponent(status)}&kind=${encodeURIComponent(kind)}`;
      void fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || "/api", path, orgId), {
        headers: { Authorization: `Bearer ${token}` },
      }).then(async (response) => {
        if (!active || getActiveOrgId() !== orgId) return;
        const body = await response.json().catch(() => ({}));
        setPage(readProposalQueueResponse(response.ok, body));
      }).catch(() => {
        if (active && getActiveOrgId() === orgId) setPage({ items: [], counts: null, error: "CLOUD_ERROR" });
      });
    };
    load();
    window.addEventListener("ds-active-organization-changed", load);
    return () => {
      active = false;
      window.removeEventListener("ds-active-organization-changed", load);
    };
  }, [kind, refreshKey, status, token]);

  useEffect(() => {
    if (!confirmMode || !token) return;
    let active = true;
    setLoadingUsers(true);
    void fetch("/api/organizations/current/users", {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!active) return;
      setUsers(response.ok && Array.isArray(body.users) ? body.users : []);
    }).catch(() => {
      if (active) setUsers([]);
    }).finally(() => {
      if (active) setLoadingUsers(false);
    });
    return () => {
      active = false;
    };
  }, [confirmMode, token]);

  const act = (path: string, body: Record<string, unknown>) => {
    const orgId = getActiveOrgId();
    if (!token || !orgId) return;
    void fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || "/api", path, orgId), {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then(async (response) => {
      if (getActiveOrgId() !== orgId) return;
      const payload = await response.json().catch(() => ({}));
      const failed = Number(payload.failed || 0);
      setMessage(failed ? t("gcpProposals.partial", "Some candidates were not registered.") : t("gcpProposals.updated", "Registration queue updated."));
      setSelected([]);
      setConfirmMode(null);
      setRefreshKey((current) => current + 1);
    }).catch(() => setMessage(t("gcpProposals.partial", "Some candidates were not registered.")));
  };

  const items = Array.isArray(page.items) ? page.items : [];
  const visible = filterProposalRows(items, query);
  const counts = page.counts || {};
  const vmView = kind === "INSTANCE";
  const columns = proposalTableColumns(kind);
  const selectedRows = items.filter((row: Proposal) => selected.includes(row.id));
  const canRegister = selectedRows.length > 0 && selectedRows.every((row: Proposal) => row.eligible);
  const actions = proposalActionVisibility(status);
  const matchingCount = matchingRegistrationCount(page.counts);
  const countText = (value: number | undefined) => (page.error ? "—" : value ?? 0);
  const newCount = countText(vmView ? counts.pendingVms ?? page.totalNew : counts.new ?? page.totalNew);
  const decidedCount = countText(vmView ? counts.decidedVms : counts.decided);
  const ignoredCount = countText(vmView ? counts.ignoredVms : counts.ignored);
  const fromInventory = items.some((row: Proposal) => row.inventoryOnly);
  const offerServerCategory = showServerCategoryAction(selectedRows);

  const statusLabel = (value: string) => {
    if (value === "REGISTERED") return t("gcpProposals.registered", "Registered");
    if (value === "LINKED") return t("gcpProposals.linked", "Linked");
    if (value === "IGNORED") return t("gcpProposals.ignored", "Ignored");
    if (value === "NEW") return t("gcpProposals.new", "New");
    return value;
  };

  const statusBadgeClass = (value: string) => {
    const tone = value === "REGISTERED" || value === "LINKED"
      ? listStyles.badgeCompleted
      : value === "NEW"
        ? listStyles.badgePlanned
        : listStyles.badgeDefault;
    return `${listStyles.badge} ${tone}`;
  };

  const register = (mode: "selected" | "all") => {
    act("/asset-proposals/register", registrationBody({
      mode,
      ids: selectedRows.map((row: Proposal) => row.id),
      kind,
      environment,
      criticality,
      ownerId,
    }));
  };

  const renderCell = (column: string, row: Proposal) => {
    if (column === "select") {
      return (
        <input
          type="checkbox"
          aria-label={row.resourceName}
          checked={selected.includes(row.id)}
          onChange={(event) => setSelected((current) => event.target.checked ? [...current, row.id] : current.filter((id) => id !== row.id))}
        />
      );
    }
    if (column === "name") {
      return row.resourceName || "—";
    }
    if (column === "project") return row.projectId || "—";
    if (column === "kind") return row.kind || "—";
    if (column === "category") {
      const presentation = categoryPresentation(row.proposedCategory);
      const label = presentation.labelKey ? t(presentation.labelKey, presentation.fallback) : (presentation.fallback || "—");
      return <span title={row.classificationBasis || undefined}>{label}</span>;
    }
    if (column === "zone") return row.location || "—";
    if (column === "ip") return row.ip || "—";
    if (column === "status") return <span className={statusBadgeClass(row.status)}>{statusLabel(row.status)}</span>;
    if (row.assetId) return <Link to={`/assets/${row.assetId}`}>{t("gcpIntegration.openAsset", "Open asset")}</Link>;
    return "—";
  };

  const tabs = [
    { id: "NEW", label: t("gcpProposals.tabNew", "New"), count: newCount },
    { id: "DECIDED", label: t("gcpProposals.tabRegistered", "Registered"), count: decidedCount },
    { id: "IGNORED", label: t("gcpProposals.tabIgnored", "Ignored"), count: ignoredCount },
  ];

  return (
    <GovernanceListingSection
      title={t("gcpProposals.title", "Discovered assets")}
      description={t("gcpProposals.summary", "Virtual machines from the collected inventory. Nothing is added until you confirm.")}
    >
      {page.notice && !noticeHidden ? (
        <p className={listStyles.listRowMeta}>
          {t("gcpProposals.notice", "Notice")}: {page.notice.projectId || "—"}
          {" "}
          <Button type="button" size="small" color="outline" onClick={() => setNoticeHidden(true)}>{t("gcpProposals.dismissNotice", "Dismiss notice")}</Button>
        </p>
      ) : null}
      {page.error ? <p>{t("gcpProposals.loadError", "The discovered-asset queue could not be loaded.")} ({page.error})</p> : null}
      {message ? <p>{message}</p> : null}
      <div className={listStyles.segmentedTabs} role="tablist" aria-label={t("gcpChecks.status", "Status")}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={status === tab.id}
            className={`${listStyles.segmentedTab} ${status === tab.id ? listStyles.segmentedTabActive : ""}`}
            title={tab.id === "DECIDED" ? t("gcpProposals.decided", "Registered or linked") : undefined}
            onClick={() => {
              setStatus(tab.id);
              setConfirmMode(null);
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>
      <div className={`${listStyles.filtersRow} ${listStyles.sectionSpacing}`}>
        <TextField
          label={t("gcpProposals.search", "Search loaded rows")}
          value={query}
          onChange={setQuery}
          placeholder={t("gcpProposals.searchPlaceholder", "Name, project, or IP")}
          aria-label={t("gcpProposals.search", "Search loaded rows")}
          minWidth="14rem"
        />
        <Select
          label={t("gcpIntegration.kind", "Kind")}
          minWidth="12rem"
          variant="outline"
          value={kind}
          options={[
            { value: "INSTANCE", label: t("gcpProposals.vms", "Virtual machines") },
            { value: "", label: t("gcpProposals.allKinds", "All kinds") },
          ]}
          onChange={(value) => {
            setKind(value ?? "");
            setConfirmMode(null);
          }}
        />
      </div>
      {page.error ? null : visible.length === 0 ? (
        <p>
          {query.trim()
            ? t("gcpProposals.noMatches", "No rows match this search.")
            : t("gcpProposals.empty", "No cloud registration candidates for this filter.")}
        </p>
      ) : (
        <div className={listStyles.tableWrap}>
          <table className={`${listStyles.table} ${cardStyles.proposalTable}`}>
            <colgroup>
              {columns.map((column: string) => <col key={column} style={{ width: PROPOSAL_COLUMN_WIDTH[column] }} />)}
            </colgroup>
            <thead>
              <tr>
                {columns.map((column: string) => (
                  <th key={column} className={listStyles.th} scope="col">
                    {COLUMN_LABEL[column] ? t(COLUMN_LABEL[column][0], COLUMN_LABEL[column][1]) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((row: Proposal) => (
                <tr key={row.id} className={listStyles.row}>
                  {columns.map((column: string) => (
                    <td
                      key={column}
                      className={`${listStyles.td} ${column === "name" || column === "project" ? cardStyles.clipCell : ""}`}
                      title={column === "name" ? `${row.resourceName}${row.externalId ? ` (${row.externalId})` : ""}` : undefined}
                    >
                      {renderCell(column, row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className={listStyles.actionsGap} style={{ marginTop: "0.75rem" }}>
        {actions.addSelected ? (
          <Button type="button" size="small" color="primary" disabled={!canRegister} onClick={() => setConfirmMode("selected")}>
            {t("gcpProposals.addSelectedCount", "Add selected ({count})", { count: selectedRows.length })}
          </Button>
        ) : null}
        {actions.addMatching ? (
          <Button type="button" size="small" color="outline" disabled={Boolean(page.error) || matchingCount < 1} onClick={() => setConfirmMode("all")}>
            {t("gcpProposals.addMatchingCount", "Add all matching ({count})", { count: matchingCount })}
          </Button>
        ) : null}
        {offerServerCategory ? (
          <Button type="button" size="small" color="outline" disabled={!canRegister} onClick={() => act("/asset-proposals/category", { ids: selectedRows.map((row: Proposal) => row.id), category: "SERVERS" })}>
            {t("gcpProposals.categoryServers", "Use server category")}
          </Button>
        ) : null}
        {actions.ignore ? (
          <Button type="button" size="small" color="outline" disabled={selectedRows.length === 0} onClick={() => act("/asset-proposals/status", { ids: selectedRows.map((row: Proposal) => row.id), status: "IGNORED" })}>
            {t("gcpProposals.ignore", "Ignore")}
          </Button>
        ) : null}
        {actions.restore ? (
          <Button type="button" size="small" color="outline" disabled={selectedRows.length === 0} onClick={() => act("/asset-proposals/status", { ids: selectedRows.map((row: Proposal) => row.id), status: "NEW" })}>
            {t("gcpProposals.restore", "Return to review")}
          </Button>
        ) : null}
        {fromInventory ? (
          <Button type="button" size="small" color="outline" aria-expanded={moreOpen} onClick={() => setMoreOpen((current) => !current)}>
            {t("gcpProposals.more", "More")}
          </Button>
        ) : null}
      </div>
      {confirmMode ? (
        <div className={listStyles.listRow} style={{ marginTop: "0.75rem" }}>
          <div className={listStyles.listRowMain}>
            <p className={listStyles.listRowTitle}>{t("gcpProposals.registerForm", "Registration settings")}</p>
            <p className={listStyles.listRowMeta}>
              {confirmMode === "all"
                ? t("gcpProposals.confirmCount", "This registers {count} new eligible virtual machines, including rows outside this page.", { count: matchingCount })
                : t("gcpProposals.confirmSelected", "These settings apply to {count} selected virtual machines.", { count: selectedRows.length })}
            </p>
            <div className={listStyles.formGrid2}>
              <Select
                label={t("assets.add.environment", "Environment")}
                minWidth="100%"
                value={environment}
                options={[
                  { value: "PRODUCTION", label: t("assets.add.production", "Production") },
                  { value: "STAGING", label: t("assets.add.staging", "Staging") },
                  { value: "DEVELOPMENT", label: t("assets.add.development", "Development") },
                  { value: "TEST", label: t("assets.add.test", "Test") },
                ]}
                onChange={(value) => value != null && setEnvironment(value)}
              />
              <Select
                label={t("assets.add.criticality", "Criticality")}
                minWidth="100%"
                value={criticality}
                options={[
                  { value: "", label: t(CRITICALITY_EMPTY_LABEL, "Not Set") },
                  { value: "LOW", label: t("assets.add.criticalityLow", "Low") },
                  { value: "MEDIUM", label: t("assets.add.criticalityMedium", "Medium") },
                  { value: "HIGH", label: t("assets.add.criticalityHigh", "High") },
                  { value: "CRITICAL", label: t("assets.add.criticalityCritical", "Critical") },
                ]}
                onChange={(value) => setCriticality(value ?? "")}
              />
              <Select
                label={t("assets.add.owner", "Owner")}
                minWidth="100%"
                disabled={loadingUsers}
                value={ownerId}
                options={[
                  { value: "", label: t("assets.add.noOwner", "No Owner") },
                  ...users.map((user) => ({
                    value: user.id,
                    label: `${user.username}${user.email ? ` (${user.email})` : ""}`,
                  })),
                ]}
                onChange={(value) => setOwnerId(value ?? "")}
              />
            </div>
            <div className={listStyles.actionsGap} style={{ marginTop: "0.75rem" }}>
              <Button
                type="button"
                size="small"
                color="primary"
                disabled={confirmMode === "selected" ? !canRegister : Boolean(page.error) || matchingCount < 1}
                onClick={() => register(confirmMode)}
              >
                {t("common.confirm", "Confirm")}
              </Button>
              <Button type="button" size="small" color="outline" onClick={() => setConfirmMode(null)}>
                {t("common.cancel", "Cancel")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      {moreOpen && fromInventory ? (
        <p className={listStyles.listRowMeta}>
          {t("gcpProposals.inventoryNote", "These virtual machines are already in the collected inventory. Recording the queue does not register an asset.")}
          {" "}
          <Button type="button" size="small" color="outline" onClick={() => act("/asset-proposals/reconcile", {})}>
            {t("gcpProposals.recordInventory", "Record inventory queue")}
          </Button>
        </p>
      ) : null}
      {page.nextCursor ? <Button type="button" size="small" color="outline" onClick={() => {
        const orgId = getActiveOrgId();
        if (!token || !orgId || !page.nextCursor) return;
        const path = `/asset-proposals?status=${encodeURIComponent(status)}&kind=${encodeURIComponent(kind)}&cursor=${encodeURIComponent(page.nextCursor)}`;
        void fetch(buildCloudRequestUrl(import.meta.env.VITE_API_URL || "/api", path, orgId), { headers: { Authorization: `Bearer ${token}` } }).then(async (response) => {
          if (getActiveOrgId() !== orgId) return;
          const body = await response.json().catch(() => ({}));
          const incoming = readProposalQueueResponse(response.ok, body);
          if (incoming.error) {
            setPage((current) => ({ ...current, error: incoming.error }));
            return;
          }
          const incomingItems = Array.isArray(incoming.items) ? incoming.items : [];
          setPage((current) => ({
            ...current,
            ...incoming,
            error: null,
            items: [...current.items, ...incomingItems.filter((row: Proposal) => !current.items.some((item) => item.id === row.id))],
          }));
        });
      }}>{t("gcpIntegration.loadMore", "Load more")}</Button> : null}
    </GovernanceListingSection>
  );
}
